// Legal pages for ShiftMateRN — Privacy Policy, Terms of Service, Medical Disclaimer
// Last updated: replace EFFECTIVE_DATE below with the date you publish.

const EFFECTIVE_DATE = "May 13, 2026";
const COMPANY = "ShiftMateRN";
const OWNER = "Eniola Fadairo";
const SUPPORT_EMAIL = "support@rn101hq.com";

const styles = {
  page: { maxWidth: 760, margin: "0 auto", padding: "40px 24px 80px", color: "#fff", fontFamily: "'Poppins',sans-serif" },
  back: { background: "transparent", border: "none", color: "rgba(255,255,255,.4)", cursor: "pointer", fontSize: 14, marginBottom: 28, fontFamily: "'Poppins',sans-serif" },
  h1: { fontSize: 32, fontWeight: 900, color: "#fff", letterSpacing: -1, marginBottom: 8 },
  meta: { fontSize: 13, color: "rgba(255,255,255,.4)", marginBottom: 32 },
  h2: { fontSize: 18, fontWeight: 800, color: "#fff", marginTop: 28, marginBottom: 10 },
  p: { fontSize: 14, color: "rgba(255,255,255,.78)", lineHeight: 1.75, marginBottom: 14 },
  ul: { fontSize: 14, color: "rgba(255,255,255,.78)", lineHeight: 1.75, marginBottom: 14, paddingLeft: 22 },
  li: { marginBottom: 6 },
  callout: { background: "rgba(255,45,120,.08)", border: "1.5px solid rgba(255,45,120,.3)", borderRadius: 12, padding: "14px 18px", fontSize: 13, lineHeight: 1.7, color: "rgba(255,255,255,.85)", marginTop: 14, marginBottom: 18 },
  code: { background: "rgba(255,255,255,.06)", padding: "2px 6px", borderRadius: 4, fontSize: 13, fontFamily: "monospace" },
  link: { color: "#00C8E8", textDecoration: "none", fontWeight: 600 },
};

function Back({ onBack }) {
  return <button onClick={onBack} style={styles.back}>← Back</button>;
}

// ─────────────────────────────────────────────
// PRIVACY POLICY
// ─────────────────────────────────────────────

export function PrivacyPolicy({ onBack }) {
  return (
    <div style={styles.page}>
      <Back onBack={onBack} />
      <h1 style={styles.h1}>Privacy Policy</h1>
      <div style={styles.meta}>Effective {EFFECTIVE_DATE}</div>

      <p style={styles.p}>
        {COMPANY} ("we," "us," "our") respects your privacy. This policy explains what information we collect when you use our website and AI tools (the "Service"), how we use it, who we share it with, and the rights you have over it.
      </p>

      <h2 style={styles.h2}>1. Who we are</h2>
      <p style={styles.p}>
        {COMPANY} is an AI-powered educational and reference platform built for nursing students and licensed nurses, owned and operated by {OWNER}. The Service is operated from the United States. Contact: <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>.
      </p>
      <div style={styles.callout}>
        <strong>HIPAA notice:</strong> {COMPANY} is NOT a HIPAA-covered entity and is NOT HIPAA compliant. Do not enter any protected health information (PHI) — including patient names, MRNs, dates of birth, or any identifying details. Always use generic, de-identified descriptions.
      </div>

      <h2 style={styles.h2}>2. Information we collect</h2>
      <p style={styles.p}>We collect only what's needed to operate the Service:</p>
      <ul style={styles.ul}>
        <li style={styles.li}><strong>Account information</strong> — your name, email, and a hashed (one-way encrypted) password when you create an account.</li>
        <li style={styles.li}><strong>Usage data</strong> — the number of AI sessions you use per month, the tool you used, and saved responses you choose to store.</li>
        <li style={styles.li}><strong>Conversation content</strong> — the questions you submit to our AI tools and the responses generated. These are processed in real time to deliver answers and may be stored if you click "Save."</li>
        <li style={styles.li}><strong>Payment information</strong> — handled exclusively by Stripe. We never see, store, or have access to your full card number. We receive only a transaction confirmation, your billing email, and your subscription plan.</li>
        <li style={styles.li}><strong>Technical data</strong> — basic browser and device information needed to operate the site (e.g., browser type, IP address). This is typical for any website.</li>
      </ul>

      <div style={styles.callout}>
        <strong>What we do NOT collect:</strong> We do not collect protected health information (PHI). You should never enter real patient names, medical record numbers, or any directly identifying patient details into the Service. Always describe scenarios in generic, de-identified terms.
      </div>

      <h2 style={styles.h2}>3. How we use your information</h2>
      <ul style={styles.ul}>
        <li style={styles.li}>To create and authenticate your account.</li>
        <li style={styles.li}>To deliver AI-generated responses to your questions.</li>
        <li style={styles.li}>To save responses you choose to bookmark.</li>
        <li style={styles.li}>To process payments and manage your subscription status.</li>
        <li style={styles.li}>To send transactional emails (password resets, payment receipts, confirmations).</li>
        <li style={styles.li}>To diagnose technical issues, prevent fraud, and improve the Service.</li>
        <li style={styles.li}>To communicate with you when you contact our support address.</li>
      </ul>

      <h2 style={styles.h2}>4. Service providers we share data with</h2>
      <p style={styles.p}>
        We use third-party service providers to operate the Service. They process your data only on our behalf and only to the extent necessary to provide their service. They are:
      </p>
      <ul style={styles.ul}>
        <li style={styles.li}><strong>Supabase</strong> — account authentication and data storage. (<a href="https://supabase.com/privacy" style={styles.link} target="_blank" rel="noreferrer">supabase.com/privacy</a>)</li>
        <li style={styles.li}><strong>Anthropic</strong> — AI model that generates the responses. Your questions are sent to Anthropic's API for processing. (<a href="https://www.anthropic.com/legal/privacy" style={styles.link} target="_blank" rel="noreferrer">anthropic.com/legal/privacy</a>)</li>
        <li style={styles.li}><strong>Stripe</strong> — payment processing. (<a href="https://stripe.com/privacy" style={styles.link} target="_blank" rel="noreferrer">stripe.com/privacy</a>)</li>
        <li style={styles.li}><strong>Resend</strong> — transactional email delivery. (<a href="https://resend.com/legal/privacy-policy" style={styles.link} target="_blank" rel="noreferrer">resend.com/legal/privacy-policy</a>)</li>
        <li style={styles.li}><strong>Vercel</strong> — website hosting. (<a href="https://vercel.com/legal/privacy-policy" style={styles.link} target="_blank" rel="noreferrer">vercel.com/legal/privacy-policy</a>)</li>
      </ul>
      <p style={styles.p}>
        We do not sell, rent, or trade your personal data to anyone. We do not use your data to serve advertising. We do not share your data for any purpose not listed in this policy.
      </p>

      <h2 style={styles.h2}>5. Data retention</h2>
      <p style={styles.p}>
        We keep your account and saved data for as long as your account is active. If you delete your account, we delete your personal data within 30 days, except for limited records we are required by law to retain (such as tax and payment records, which Stripe retains per their policy).
      </p>

      <h2 style={styles.h2}>6. Your rights</h2>
      <p style={styles.p}>
        You can request the following at any time by emailing <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>:
      </p>
      <ul style={styles.ul}>
        <li style={styles.li}>A copy of the personal data we hold about you.</li>
        <li style={styles.li}>Correction of inaccurate information.</li>
        <li style={styles.li}>Deletion of your account and associated data.</li>
        <li style={styles.li}>Withdrawal of consent for non-essential processing.</li>
      </ul>
      <p style={styles.p}>
        Residents of California, the EU, the UK, and other jurisdictions with applicable privacy laws have additional rights under those laws. You may exercise these rights by contacting us.
      </p>

      <h2 style={styles.h2}>7. Children</h2>
      <p style={styles.p}>
        The Service is not directed to children under 13. We do not knowingly collect personal information from children under 13. Users between 13 and 18 should have parental or guardian consent before creating an account.
      </p>

      <h2 style={styles.h2}>8. Security</h2>
      <p style={styles.p}>
        We use industry-standard practices to protect your information, including encrypted connections (HTTPS), secure password hashing, and reputable service providers. No system is 100% secure, but we work to make this one as secure as practical.
      </p>

      <h2 style={styles.h2}>9. Changes to this policy</h2>
      <p style={styles.p}>
        We may update this policy from time to time. If we make material changes, we will notify you by email or through a notice in the Service before the change takes effect. The "Effective" date at the top of this policy will always reflect the current version.
      </p>

      <h2 style={styles.h2}>10. Contact us</h2>
      <p style={styles.p}>
        Questions about this policy or your data? Email <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────
// TERMS OF SERVICE
// ─────────────────────────────────────────────

export function TermsOfService({ onBack }) {
  return (
    <div style={styles.page}>
      <Back onBack={onBack} />
      <h1 style={styles.h1}>Terms of Service</h1>
      <div style={styles.meta}>Effective {EFFECTIVE_DATE}</div>

      <p style={styles.p}>
        Welcome to {COMPANY}. These Terms of Service ("Terms") govern your access to and use of our website, AI tools, and related services (collectively, the "Service"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.
      </p>

      <h2 style={styles.h2}>1. Eligibility</h2>
      <p style={styles.p}>
        You must be at least 18 years old to create an account and pay for the Service, or between 13 and 18 with verified consent from a parent or guardian. The Service is intended for nursing students, licensed nurses, and individuals with related professional or educational interests in nursing.
      </p>

      <h2 style={styles.h2}>2. The Service</h2>
      <p style={styles.p}>
        {COMPANY} is an educational and reference tool that uses AI to help nurses and nursing students think through clinical, academic, and career-related scenarios. <strong>The Service is not medical advice, not clinical decision support, and not a substitute for professional judgment, hospital protocols, or supervision by a licensed clinician.</strong> See our Medical & AI Educational Disclaimer for full details.
      </p>

      <h2 style={styles.h2}>3. Your account</h2>
      <p style={styles.p}>
        You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately at <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a> if you suspect unauthorized access. You must provide accurate information when creating your account.
      </p>

      <h2 style={styles.h2}>4. Acceptable use</h2>
      <p style={styles.p}>You agree NOT to:</p>
      <ul style={styles.ul}>
        <li style={styles.li}>Enter protected health information (PHI), patient names, medical record numbers, or any directly identifying patient details into the Service.</li>
        <li style={styles.li}>Use the Service to make actual clinical decisions about a real patient without independently verifying with your supervising clinician, hospital protocol, and primary clinical references.</li>
        <li style={styles.li}>Attempt to extract, scrape, or copy responses for resale or redistribution.</li>
        <li style={styles.li}>Use the Service to generate content that is illegal, harassing, defamatory, or harmful to any person.</li>
        <li style={styles.li}>Reverse-engineer, decompile, or attempt to access the underlying source code or AI models.</li>
        <li style={styles.li}>Share your account credentials with others or allow others to use your account.</li>
        <li style={styles.li}>Use the Service in any way that violates applicable laws or regulations, including HIPAA, your state's nurse practice act, or your employer's policies.</li>
      </ul>
      <p style={styles.p}>
        Violation of these rules may result in immediate suspension or termination of your account without refund.
      </p>

      <h2 style={styles.h2}>5. Subscriptions, billing, and refunds</h2>
      <ul style={styles.ul}>
        <li style={styles.li}><strong>Free plan:</strong> 5 free AI sessions (one-time trial) at no charge.</li>
        <li style={styles.li}><strong>Pro plan ($12/month):</strong> Unlimited sessions billed monthly. Auto-renews each month until you cancel. You may cancel any time from your account; cancellation takes effect at the end of the current billing period (no prorated refund for partial months).</li>
        <li style={styles.li}><strong>Founding Member ($97 one-time — lifetime):</strong> Lifetime unlimited Pro access to all 24 tools plus included digital products. Refunds available within 7 days of purchase only if no digital products have been downloaded and fewer than 5 AI sessions have been used. After that point, this plan is non-refundable.</li>
        <li style={styles.li}>All prices are in U.S. dollars. Applicable taxes may be added at checkout.</li>
        <li style={styles.li}>Payment is processed by Stripe. We do not store your full card number.</li>
        <li style={styles.li}>For refund requests within the windows above, email <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>.</li>
      </ul>

      <h2 style={styles.h2}>6. Intellectual property</h2>
      <p style={styles.p}>
        We own the Service, including its design, code, system prompts, and brand. AI-generated responses are produced by third-party AI models in response to your inputs. You may use the responses you generate for your own personal study, professional reference, and learning. You may not redistribute, resell, or use them to train competing AI services.
      </p>
      <p style={styles.p}>
        You retain ownership of the content you input. By submitting content, you grant us a limited license to process it through our service providers (e.g., Anthropic) for the sole purpose of generating a response.
      </p>

      <h2 style={styles.h2}>7. Disclaimers — please read</h2>
      <p style={styles.p}>
        <strong>The Service is provided "as is" and "as available" without warranties of any kind</strong>, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, accuracy, or non-infringement.
      </p>
      <p style={styles.p}>
        AI-generated responses may contain errors, omissions, or outdated information. You are solely responsible for evaluating the accuracy, appropriateness, and clinical applicability of any output before relying on it. Always defer to your supervising clinician, your facility's policies, primary clinical references, and your own professional judgment.
      </p>
      <p style={styles.p}>
        See our full Medical & AI Educational Disclaimer for additional terms specific to clinical use.
      </p>

      <h2 style={styles.h2}>8. Limitation of liability</h2>
      <p style={styles.p}>
        To the fullest extent permitted by law, {COMPANY}, its founders, employees, and service providers shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, or goodwill, arising from your use of the Service.
      </p>
      <p style={styles.p}>
        Our total liability to you for any claim arising from or relating to the Service shall not exceed the greater of (a) the amount you paid us in the 12 months preceding the claim, or (b) one hundred U.S. dollars ($100).
      </p>

      <h2 style={styles.h2}>9. Indemnification</h2>
      <p style={styles.p}>
        You agree to defend, indemnify, and hold harmless {COMPANY} and its founders, employees, and service providers from any claim, damages, or expense (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your violation of any law or third-party right — including any claim relating to your use of AI-generated content in a clinical or professional setting.
      </p>

      <h2 style={styles.h2}>10. Termination</h2>
      <p style={styles.p}>
        You may delete your account at any time by emailing <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>. We may suspend or terminate your account at any time for violation of these Terms, fraud, or abuse, with or without notice.
      </p>

      <h2 style={styles.h2}>11. Governing law and disputes</h2>
      <p style={styles.p}>
        These Terms are governed by the laws of the State in which {COMPANY} is established, without regard to conflicts-of-law principles. Any dispute arising from these Terms or your use of the Service shall be resolved in the state or federal courts located in that jurisdiction, and you consent to that venue.
      </p>

      <h2 style={styles.h2}>12. Changes to these Terms</h2>
      <p style={styles.p}>
        We may update these Terms from time to time. If we make material changes, we will notify you by email or through a notice in the Service before the change takes effect. Continued use of the Service after the effective date constitutes acceptance of the new Terms.
      </p>

      <h2 style={styles.h2}>13. Contact</h2>
      <p style={styles.p}>
        Questions about these Terms? Email <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────
// MEDICAL & AI EDUCATIONAL DISCLAIMER
// ─────────────────────────────────────────────

export function Disclaimer({ onBack }) {
  return (
    <div style={styles.page}>
      <Back onBack={onBack} />
      <h1 style={styles.h1}>Medical & AI Educational Disclaimer</h1>
      <div style={styles.meta}>Effective {EFFECTIVE_DATE}</div>

      <div style={styles.callout}>
        <strong>Read this carefully.</strong> By using {COMPANY}, you agree to these terms. They are designed to protect you, your patients, and your nursing license.
      </div>

      <h2 style={styles.h2}>1. Educational use only</h2>
      <p style={styles.p}>
        {COMPANY} is an <strong>educational and reference tool</strong> for nursing students and licensed nurses. It is intended to support learning, study, professional reflection, and general clinical reasoning practice. <strong>It is not a clinical decision support system, electronic health record, or medical device.</strong> It has not been reviewed, approved, or cleared by the FDA, any state nursing board, or any regulatory body.
      </p>

      <h2 style={styles.h2}>2. Not medical advice</h2>
      <p style={styles.p}>
        Nothing produced by the Service constitutes medical advice, professional advice, diagnosis, treatment recommendation, or a substitute for the judgment of a licensed healthcare provider. AI-generated responses must never replace:
      </p>
      <ul style={styles.ul}>
        <li style={styles.li}>Clinical orders from a licensed prescriber.</li>
        <li style={styles.li}>Your facility's policies, procedures, and protocols.</li>
        <li style={styles.li}>Your supervising preceptor, charge nurse, or attending physician.</li>
        <li style={styles.li}>Authoritative drug references, lab references, and clinical guidelines.</li>
        <li style={styles.li}>Your own professional nursing judgment.</li>
      </ul>

      <h2 style={styles.h2}>3. AI has limitations</h2>
      <p style={styles.p}>
        The AI behind {COMPANY} can produce responses that are inaccurate, incomplete, biased, outdated, or fabricated ("hallucinated"). This is true of all current AI systems, including ours. <strong>You are responsible for independently verifying every clinical fact, dose, calculation, drug interaction, and recommendation</strong> against trusted primary references before relying on it for any care decision.
      </p>

      <h2 style={styles.h2}>4. Your professional responsibility</h2>
      <p style={styles.p}>
        If you are a licensed nurse or nursing student, you are bound by your state's nurse practice act, your facility's policies, and your professional duty to your patients. Those obligations override anything the Service produces. You agree not to use the Service in any way that would conflict with those obligations.
      </p>
      <p style={styles.p}>
        If a response from the Service contradicts your facility protocol, your prescriber's orders, or your professional judgment, <strong>follow your facility, prescriber, or judgment — not the Service.</strong>
      </p>

      <h2 style={styles.h2}>5. No protected health information (PHI)</h2>
      <p style={styles.p}>
        Do not enter real patient names, medical record numbers, dates of birth, addresses, or any directly identifying information about a real patient into the Service. Describe scenarios in generic, de-identified terms (e.g., "a 58-year-old female with CHF" rather than a real patient's name). Entering PHI may violate HIPAA, your facility's policies, and these Terms.
      </p>

      <h2 style={styles.h2}>6. Emergencies — call for help, not the AI</h2>
      <div style={styles.callout}>
        If a patient is experiencing a medical emergency, deterioration, or requires immediate intervention, <strong>call your rapid response team, code team, or 911 immediately. Do not wait for or rely on AI output in any time-sensitive clinical situation.</strong>
      </div>

      <h2 style={styles.h2}>7. Pharmacology and dosing</h2>
      <p style={styles.p}>
        Medication doses, drug interactions, and pharmacology information presented by the Service must be verified against an authoritative drug reference (e.g., Lexicomp, Micromedex, your hospital pharmacy, or the manufacturer's package insert) before being used to inform any patient care. Even a small AI error in dosing can cause patient harm.
      </p>

      <h2 style={styles.h2}>8. NCLEX and exam content</h2>
      <p style={styles.p}>
        AI-generated practice questions are educational examples, not actual NCLEX content. They are not affiliated with, endorsed by, or representative of any nursing licensure body, including the National Council of State Boards of Nursing (NCSBN). Real NCLEX content is proprietary to NCSBN.
      </p>

      <h2 style={styles.h2}>9. No professional relationship</h2>
      <p style={styles.p}>
        Use of the Service does not create a nurse-patient, provider-patient, instructor-student, employer-employee, or any other professional or fiduciary relationship between you and {COMPANY} or its founders.
      </p>

      <h2 style={styles.h2}>10. Mental health and crisis</h2>
      <p style={styles.p}>
        Our Burnout Check-In tool is intended for general reflective support and is not a substitute for mental health care. If you are experiencing a mental health crisis, suicidal thoughts, or intent to harm yourself or another person, please contact a qualified mental health professional, your Employee Assistance Program (EAP), or a crisis line (in the U.S., call or text 988 for the Suicide and Crisis Lifeline) immediately.
      </p>

      <h2 style={styles.h2}>11. Acknowledgement</h2>
      <p style={styles.p}>
        By using {COMPANY}, you acknowledge that you have read, understood, and agree to this disclaimer in addition to our Terms of Service and Privacy Policy.
      </p>

      <h2 style={styles.h2}>12. Contact</h2>
      <p style={styles.p}>
        Questions about this disclaimer? Email <a href={`mailto:${SUPPORT_EMAIL}`} style={styles.link}>{SUPPORT_EMAIL}</a>.
      </p>
    </div>
  );
}
